'use client'
import type { ColumnDefWithSkeleton } from '@morpho-org/ui-v6/types'

import { CellTokenAmountPrice } from '@morpho-org/ui-v6/client'
import { CellSimpleText } from '@morpho-org/ui-v6/server'
import { Skeleton, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui'
import { abbreviateAddress } from '@repo/utils'
import { rowSortingFeature, tableFeatures } from '@tanstack/react-table'

import { ButtonLink } from '@/components/common/ButtonLink'
import { TimestampCell } from '@/components/common/Cells/TimestampCell'

import type { VaultV2AllocationTransactionsTableRow } from './VaultV2AllocationTransactions.types'

import AllocationTargetCell from '../components/AllocationTargetCell'

// Keeps enableSorting: false effective; without rowSortingFeature, accessor headers render as sortable.
export const vaultV2AllocationTransactionsTableFeatures = tableFeatures({
  rowSortingFeature
})
export type VaultV2AllocationTransactionsTableFeatures =
  typeof vaultV2AllocationTransactionsTableFeatures

type VaultV2AllocationTransactionsTableColumnDef = ColumnDefWithSkeleton<
  VaultV2AllocationTransactionsTableFeatures,
  VaultV2AllocationTransactionsTableRow
>
export const allocationTransactionsColumnsDefs: VaultV2AllocationTransactionsTableColumnDef[] = [
  {
    accessorKey: 'timestamp',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">Date</span>
        </TooltipTrigger>
        <TooltipContent>Timestamp of when the transaction occurred.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => <TimestampCell timestamp={row.original.timestamp} />,
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[122px] rounded-[20px]" />
  },
  {
    accessorKey: 'sender',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '7px' }}>
            Caller
          </span>
        </TooltipTrigger>
        <TooltipContent>The address that executed the transaction.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <ButtonLink
        href={row.original.urlSender}
        mode="external"
        buttonProps={{
          variant: 'ghost',
          size: 'default',
          label: abbreviateAddress(row.original.sender),
          avatarProps: {
            address: row.original.sender
          }
        }}
      />
    ),
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[143px] rounded-[20px]" />
  },
  {
    accessorKey: 'assets',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">Amount</span>
        </TooltipTrigger>
        <TooltipContent>The amount of token involved in the transaction.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const { token, assets, assetsUsd, typeText } = row.original

      // Fall back to the event type whenever neither the amount nor the
      // value carries a sign (missing token/amount, or a zero-asset event,
      // where `0n * -1n` erases the Deallocate direction) — the direction
      // of the move must stay visible. A missing token or amount still
      // renders when a signed USD valuation is known: the cell shows its
      // `-` placeholder next to the signed value.
      const hasDirectionalAmount = token != null && assets != null && assets !== 0n
      const hasDirectionalValue = assetsUsd != null && assetsUsd !== 0n
      if (!hasDirectionalAmount && !hasDirectionalValue) {
        return <CellSimpleText>{typeText}</CellSimpleText>
      }

      return (
        <CellTokenAmountPrice
          token={token ?? undefined}
          amount={assets}
          value={assetsUsd ?? undefined}
          withAvatar
          sign
        />
      )
    },
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[190px] rounded-[20px]" />
  },
  {
    accessorKey: 'target',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '7px' }}>
            Vault / Market
          </span>
        </TooltipTrigger>
        <TooltipContent>The vault or market funds were allocated to or from.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <AllocationTargetCell
        target={row.original.target}
        chainId={row.original.chainId}
        logoURI={row.original.token?.logoURI}
      />
    ),
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[240px] rounded-[20px]" />
  },
  {
    accessorKey: 'hash',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '10px' }}>
            Hash
          </span>
        </TooltipTrigger>
        <TooltipContent>The transaction hash.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <ButtonLink
        href={row.original.urlHash}
        mode="external"
        buttonProps={{
          variant: 'ghost',
          size: 'default',
          label: abbreviateAddress(row.original.hash)
        }}
      />
    ),
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[122px] rounded-[20px]" />
  }
]
